<template>
    <page-layout :hero-image-url="brandLogo" :body-style="{ paddingTop: 0 }">
        <g-loader v-if="isLoading" type="fixed" />
        <template v-else>
            <template slot="header">
                <title-bar :title="brandName" class="brand-name" />
            </template>
            <template slot="default">
                <div class="margin-bottom-normal">
                    <g-typography tag="h1" :value="campaignTitle" font-weight="semibold" />
                </div>
                <template>
                    <template v-if="campaignObjective">
                        <div class="margin-bottom-small">
                            <g-typography tag="h4" value="Overview" font-weight="semibold" />
                        </div>
                        <div class="margin-bottom-normal" v-html="$sanitize(campaignObjective)" />
                    </template>
                    <template v-if="proposalEnd || contentEnd">
                        <div class="margin-bottom-normal">
                            <g-level>
                                <g-level-item v-if="proposalEnd">
                                    <div>
                                        <div class="margin-bottom-small">
                                            <g-typography
                                                tag="h4"
                                                :value="getSectionTitle('campaign', 'submit-proposal-by')"
                                                font-weight="semibold"
                                            />
                                        </div>
                                        <div>
                                            <g-typography tag="div" :value="formatDate(proposalEnd)" />
                                        </div>
                                    </div>
                                </g-level-item>
                                <g-level-item v-if="contentEnd" slot="right">
                                    <div>
                                        <div class="margin-bottom-small">
                                            <g-typography
                                                tag="h4"
                                                :value="getSectionTitle('campaign', 'deliver-content-by')"
                                                font-weight="semibold"
                                            />
                                        </div>
                                        <div>
                                            <g-typography tag="div" :value="formatDate(contentEnd)" />
                                        </div>
                                    </div>
                                </g-level-item>
                            </g-level>
                        </div>
                    </template>
                    <template v-if="hasCompensation">
                        <div class="margin-bottom-small">
                            <g-typography
                                tag="h4"
                                :value="getSectionTitle('titles', 'compensation')"
                                font-weight="semibold"
                            />
                        </div>
                        <div class="margin-bottom-normal">
                            <g-typography
                                v-if="compensationDescription"
                                tag="p"
                                class="break-new-line"
                                :value="compensationDescription"
                            />
                        </div>
                    </template>
                    <template v-if="hasTasks">
                        <div class="margin-bottom-small">
                            <g-typography tag="h4" :value="getSectionTitle('titles', 'tasks')" font-weight="semibold" />
                        </div>
                        <div class="margin-bottom-normal">
                            <template v-for="task in tasks">
                                <campaign-task :task="task" class="margin-bottom-small" />
                            </template>
                        </div>
                    </template>
                    <template v-if="hasAssets">
                        <div class="margin-bottom-small">
                            <g-typography
                                tag="h4"
                                :value="getSectionTitle('titles', 'brand-assets')"
                                font-weight="semibold"
                            />
                        </div>
                        <div class="margin-bottom-normal">
                            <template v-for="asset in assets">
                                <brand-asset :asset="asset" class="margin-bottom-small" />
                            </template>
                        </div>
                    </template>
                    <template v-if="brandDescription">
                        <g-divider />
                        <div class="margin-bottom-small">
                            <g-typography tag="h4" :value="`About ${brandName}`" font-weight="semibold" />
                        </div>
                        <div class="margin-bottom-normal">
                            <g-typography tag="p" :value="brandDescription" />
                        </div>
                    </template>
                </template>
            </template>
            <template slot="footer">
                <campaign-overview-footer :title="footerTitle" :subtitle="footerSubtitle">
                    <g-button
                        v-if="incompleteProposal"
                        shape="round"
                        type="primary"
                        theme="live"
                        :block="!isProposalEndSet"
                        @click="handleContinue"
                    >
                        Continue
                    </g-button>
                    <g-button
                        v-else
                        shape="round"
                        type="primary"
                        theme="live"
                        :loading="isCreatingProposal"
                        :block="!isProposalEndSet"
                        @click="handleStart"
                    >
                        <g-typography color="white" :value="getSectionTitle('campaign', 'get-started')" />
                    </g-button>
                </campaign-overview-footer>
                <div class="powered-by-grin">
                    <powered-by-grin :clickable="true" />
                </div>
            </template>
        </template>
    </page-layout>
</template>

<script>
    import * as _ from 'lodash';
    import TitleBar from '../../library/TitleBar';
    import CampaignTask from './partials/CampaignTask';
    import PageLayout from '../../layouts/PageLayout';
    import CampaignOverviewFooter from './partials/CampaignOverviewFooter';
    import moment from 'moment-timezone';
    import BrandAsset from '../../library/BrandAsset';
    import STEP_CUSTOMIZATION_KEYS from '../../../constants/proposal-step-customization-keys';
    import { getCustomizableTitle } from '../../../helpers/default-texts';
    import CompensationMixin from '../proposals/mixins/CompensationMixin';
    import {
        LIVE_CAMPAIGN_OVERVIEW_CONTINUE_EVENT,
        LIVE_CAMPAIGN_OVERVIEW_START_EVENT
    } from '../../../constants/analytics-events';
    import EventTrackingMixin from '../../../mixins/EventTrackingMixin';

    export default {
        name: 'CampaignView',
        components: {
            BrandAsset,
            CampaignOverviewFooter,
            PageLayout,
            TitleBar,
            CampaignTask
        },
        mixins: [CompensationMixin, EventTrackingMixin],
        props: {
            campaign: {
                type: Object
            },
            incompleteProposal: {
                type: Object
            },
            isCreatingProposal: {
                type: Boolean,
                default: false
            },
            isLoading: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            brandLogo() {
                return _.get(this, 'campaign.image', '');
            },
            campaignObjective() {
                return _.get(this, 'campaign.objective');
            },
            brandName() {
                return _.get(this, 'campaign.brand.name', '');
            },
            brandDescription() {
                return _.get(this, 'campaign.brand.description', '');
            },
            hasCompensation() {
                return _.get(this, 'campaign.compensation', []).length > 0;
            },
            tasks() {
                return _.get(this, 'campaign.tasks', []).sort((t1, t2) => {
                    if (t1.position < t2.position) return -1;
                    if (t1.position > t2.position) return 1;
                    return 0;
                });
            },
            hasTasks() {
                return this.tasks.length > 0;
            },
            assets() {
                return _.get(this, 'campaign.assets', []);
            },
            hasAssets() {
                return this.assets.length > 0;
            },
            campaignTitle() {
                const title = _.get(this, 'campaign.influencer_title', '');
                return title ? title : _.get(this, 'campaign.title', '');
            },
            proposalStart() {
                return _.get(this, 'campaign.date_proposals_start', null);
            },
            proposalEnd() {
                return _.get(this, 'campaign.date_proposals_end', null);
            },
            contentStart() {
                return _.get(this, 'campaign.date_content_start', null);
            },
            contentEnd() {
                return _.get(this, 'campaign.date_content_end', null);
            },
            isProposalEndSet() {
                return this.proposalEnd !== null;
            },
            percentComplete() {
                const steps = _.get(this, 'incompleteProposal.steps', []);
                const totalSteps = steps.length || 1;

                const completedSteps = steps.reduce((result, step) => {
                    if (step.is_complete) {
                        result = result + 1;
                    }
                    return result;
                }, 0);

                return Math.round((completedSteps / totalSteps) * 100);
            },
            footerTitle() {
                if (this.incompleteProposal) {
                    return 'Progress';
                }

                if (this.proposalEnd) {
                    return this.getSectionTitle('campaign', 'submit-proposal-by');
                }

                return null;
            },
            footerSubtitle() {
                if (this.incompleteProposal) {
                    return `${this.percentComplete}% Complete`;
                }

                if (this.proposalEnd) {
                    return this.formatDate(this.proposalEnd);
                }

                return null;
            }
        },
        methods: {
            handleStart() {
                this.$emit('create-proposal');
                this.$bus.$emit(LIVE_CAMPAIGN_OVERVIEW_START_EVENT);
                this.trackEventProposalStarted(this.campaign.id);
            },
            handleContinue() {
                this.$router.push({
                    name: 'v2.campaign.proposal',
                    params: { proposal_id: this.incompleteProposal.id }
                });
                this.$bus.$emit(LIVE_CAMPAIGN_OVERVIEW_CONTINUE_EVENT);
                this.trackEventProposalContinue(this.campaign.id);
            },
            formatDate(isoDate) {
                return moment(isoDate).format('MMM D, YYYY');
            },
            getSectionTitle(section, alias) {
                if (
                    STEP_CUSTOMIZATION_KEYS.hasOwnProperty('campaign') &&
                    STEP_CUSTOMIZATION_KEYS[section].hasOwnProperty(alias)
                ) {
                    return getCustomizableTitle(this.campaign, STEP_CUSTOMIZATION_KEYS[section][alias]);
                }

                return alias
                    .toLowerCase()
                    .split('-')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            }
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .powered-by-grin {
        text-align: center;
        margin-top: $g-spacing-4;
    }

    .break-new-line {
        white-space: pre-line;
    }
</style>
